import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatValueAuto, formatIndianInt, FS_HEAD_SX, FS_CELL_SX } from './fieldSalesFormat.js';
import FieldSalesPager from './FieldSalesPager.jsx';
import { usePagedRows } from './fieldSalesPaging.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const GOOD = '#16A34A';
const BAD = '#DC2626';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';
const NO_DATA_TOOLTIP = 'No data source yet';

// Full month-name order used to derive the previous month from {Mon}.
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The calendar month before `mon` (short name); '—' when unknown. */
function prevMonthOf(mon) {
  const i = MONTH_SHORT.indexOf(String(mon));
  if (i < 0) return '—';
  return MONTH_SHORT[(i + 11) % 12];
}

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** Greyed Zone cell (backend returns the state there, so Zone has no source). */
function ZoneCell() {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.5, cursor: 'not-allowed' }}
      >
        —
        <Box
          component="span"
          sx={{
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * Signed % cell with a direction arrow + delta colour (the flag state is shown
 * through colour/arrow, replacing the removed Flags column — reference style).
 */
function PctCell({ value }) {
  if (!Number.isFinite(Number(value))) return <>—</>;
  const p = Number(value);
  const up = p > 0;
  const flat = p === 0;
  const color = flat ? MUTED : up ? GOOD : BAD;
  const arrow = flat ? '' : up ? '▲ ' : '▼ ';
  return (
    <Box component="span" sx={{ color }}>
      {arrow}
      {`${up ? '+' : ''}${p.toFixed(1)}%`}
    </Box>
  );
}

/** Count card (reference-style faint card). */
function CountCard({ label, value, tone }) {
  return (
    <Box
      sx={{
        bgcolor: tone?.bg ?? '#f6f9ff',
        border: `1px solid ${tone?.border ?? BORDER}`,
        borderRadius: '12px',
        p: '12px 14px',
      }}
    >
      <Typography sx={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.4px', fontWeight: 700, color: tone?.color ?? MUTED }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '20px', fontWeight: 800, color: NAVY, mt: '2px', lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

const FLAG_TONE = {
  lost: { bg: 'rgba(220,38,38,.12)', color: '#DC2626', border: 'rgba(220,38,38,.3)' },
  mom: { bg: 'rgba(245,158,11,.16)', color: '#B45309', border: 'rgba(245,158,11,.35)' },
  vs_avg: { bg: 'rgba(46,124,246,.12)', color: '#2E7CF6', border: 'rgba(46,124,246,.3)' },
};

/**
 * Volatility Analysis tab (reference "Volatility Analysis"). Subheading "{Mon}"
 * plus the HTML's two summary lines. Count cards, then the table (HQ, Division,
 * Zone, {PrevMon}, {CurMon}, Avg ({N} MO), MoM %, vs Avg %) — flag state shown
 * via arrow/colour on the % cells, Zone greyed. Client-side paged (50/page).
 * Values in Lakhs → ₹ Cr/L.
 *
 * @param {{ query: import('@tanstack/react-query').UseQueryResult, mon: string }} props
 */
export default function FieldSalesVolatilityTab({ query, mon }) {
  const data = query?.data;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const loading = query?.isLoading || query?.isFetching;

  const multiDiv = new Set(rows.map((r) => r.division)).size > 1;
  const prevMon = prevMonthOf(mon);

  // Flag counts (derived from the rows' flags — the sample has no counts block).
  const flagCount = (flag) => rows.filter((r) => Array.isArray(r.flags) && r.flags.includes(flag)).length;
  const lostN = flagCount('lost');
  const momN = flagCount('mom');
  const vsAvgN = flagCount('vs_avg');

  // Up/down counts for the summary line — COMPUTED from the mom / vs_avg signs
  // (not provided by the API). Reported in the task response.
  const momUp = rows.filter((r) => Number(r.mom) > 0).length;
  const momDown = rows.filter((r) => Number(r.mom) < 0).length;
  const avgUp = rows.filter((r) => Number(r.vs_avg) > 0).length;
  const avgDown = rows.filter((r) => Number(r.vs_avg) < 0).length;

  const paged = usePagedRows(rows, { resetKey: `${mon}:${rows.length}` });

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mt: 1,
      }}
    >
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY, mb: '2px' }}>
        📉 Volatility Analysis
      </Typography>
      <Typography sx={{ fontSize: '13px', color: MUTED, mb: '8px' }}>{mon}</Typography>

      {query?.isError ? (
        <Alert severity="error">Failed to load the volatility analysis.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No volatile HQs for the current selection.
        </Typography>
      ) : (
        <>
          {/* Two reference summary lines. "Total HQs" and the past-month list
              are not in the data, so those tokens show a greyed "—". */}
          <Typography sx={{ fontSize: '12.5px', color: '#1B2433', mb: '2px' }}>
            <b>{formatIndianInt(rows.length)}</b> of{' '}
            <Tooltip title={NO_DATA_TOOLTIP} arrow>
              <Box component="span" sx={{ opacity: 0.5, cursor: 'not-allowed' }}>—</Box>
            </Tooltip>{' '}
            HQs volatile (&gt;±25%) · Current <b>{mon}</b> vs Last <b>{prevMon}</b> · Avg of{' '}
            <Tooltip title={NO_DATA_TOOLTIP} arrow>
              <Box component="span" sx={{ opacity: 0.5, cursor: 'not-allowed' }}>—</Box>
            </Tooltip>
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#1B2433', mb: '12px' }}>
            MoM: <Box component="span" sx={{ color: GOOD }}>▲ {momUp} up</Box> /{' '}
            <Box component="span" sx={{ color: BAD }}>▼ {momDown} down</Box> &nbsp;·&nbsp; vs Avg:{' '}
            <Box component="span" sx={{ color: GOOD }}>▲ {avgUp} up</Box> /{' '}
            <Box component="span" sx={{ color: BAD }}>▼ {avgDown} down</Box>
          </Typography>

          {/* Count cards. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' },
              gap: '12px',
              mb: '14px',
            }}
          >
            <CountCard label="Volatile HQs" value={formatIndianInt(rows.length)} />
            <CountCard label="Lost" value={formatIndianInt(lostN)} tone={FLAG_TONE.lost} />
            <CountCard label="MoM breach" value={formatIndianInt(momN)} tone={FLAG_TONE.mom} />
            <CountCard label="vs Avg breach" value={formatIndianInt(vsAvgN)} tone={FLAG_TONE.vs_avg} />
          </Box>

          {/* Table — columns: HQ, Division, Zone, {PrevMon}, {CurMon}, Avg, MoM %, vs Avg %. */}
          <TableContainer sx={{ maxHeight: 480, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_SX}>#</TableCell>
                  <TableCell sx={HEAD_SX}>HQ</TableCell>
                  {multiDiv ? <TableCell sx={HEAD_SX}>Division</TableCell> : null}
                  <TableCell sx={HEAD_SX}>Zone</TableCell>
                  <TableCell sx={HEAD_SX} align="right">{prevMon}</TableCell>
                  <TableCell sx={HEAD_SX} align="right">{mon}</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Avg</TableCell>
                  <TableCell sx={HEAD_SX} align="right">MoM %</TableCell>
                  <TableCell sx={HEAD_SX} align="right">vs Avg %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.pageRows.map((r, i) => {
                  // Row text tone follows the stronger breach (lost → red).
                  const lost = Array.isArray(r.flags) && r.flags.includes('lost');
                  return (
                    <TableRow key={`${r.hq}-${r.division}-${i}`} hover>
                      <TableCell sx={CELL_SX}>{paged.offset + i + 1}</TableCell>
                      <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: lost ? BAD : '#1B2433' }}>
                        {r.hq ?? '—'}
                      </TableCell>
                      {multiDiv ? <TableCell sx={CELL_SX}>{r.division ?? '—'}</TableCell> : null}
                      <TableCell sx={CELL_SX}><ZoneCell /></TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.last)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.cur)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.avg)}</TableCell>
                      <TableCell sx={CELL_SX} align="right"><PctCell value={r.mom} /></TableCell>
                      <TableCell sx={CELL_SX} align="right"><PctCell value={r.vs_avg} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <FieldSalesPager page={paged.page} pageCount={paged.pageCount} total={paged.total} onChange={paged.setPage} unit="HQs" />
        </>
      )}
    </Box>
  );
}
